import { eventModeLabel } from '../../shared/race-events.js';
import type { MapDefinition, PublicLobbySummary, PublicRoom } from '../../shared/types.js';
import { escapeHtml, safeColour } from '../ui/html.js';
import { t } from '../i18n/index.js';

export interface PlayStepModel {
  online: boolean;
  maps: readonly MapDefinition[];
  lobbies: readonly PublicLobbySummary[] | null;
  lobbiesError: string;
  invite: { room: PublicRoom | null; error: string } | null;
  continueHtml: string;
  eventsHtml: string;
  careerHtml: string;
  careerSummary: string;
}

export function playStepHtml(model: PlayStepModel): string {
  return `
    <div class="play-step">
      <header class="play-head">
        <div class="play-title">
          <p class="eyebrow">${t('STEP 4 OF 6')}</p>
          <h1>${t('WHERE DO YOU')} <em>${t('WANT TO RACE?')}</em></h1>
        </div>
        <div class="continue-race-slot" id="continue-race-slot">${model.continueHtml}</div>
      </header>
      <p class="continue-race-status" id="continue-race-status" role="alert"></p>
      ${model.invite ? inviteCardHtml(model) : ''}
      <div class="play-modes">
        <section class="play-mode play-join" aria-labelledby="play-join-title">
          <header><i aria-hidden="true">⇄</i><div><small>${t('MULTIPLAYER')}</small><h2 id="play-join-title">${t('JOIN')}</h2></div></header>
          <p>${t('Jump into a public lobby, or paste a private invite link from a friend.')}</p>
          <div class="lobby-browser" id="lobby-browser" aria-live="polite">${lobbyBrowserHtml(model)}</div>
          <form class="invite-entry" id="invite-entry" autocomplete="off">
            <label for="invite-input">${t('INVITE LINK')}</label>
            <span><input id="invite-input" type="text" spellcheck="false" placeholder="https://…/race/r1a2b3?invite=…" aria-describedby="invite-entry-help"><button type="submit">${t('JOIN')}</button></span>
            <small id="invite-entry-help">${t('Paste the whole link — it contains the private key.')}</small>
          </form>
        </section>
        <section class="play-mode play-host" aria-labelledby="play-host-title">
          <header><i aria-hidden="true">◎</i><div><small>${t('MULTIPLAYER')}</small><h2 id="play-host-title">${t('HOST')}</h2></div></header>
          <p>${t('Open your own lobby. Pick track, rules and AI rivals together with your crew — everyone sees changes live.')}</p>
          <ul class="play-points"><li>${t('Up to 6 drivers + AI rivals')}</li><li>${t('Private link by default, go public any time')}</li><li>${t('Kick, ban and force start as host')}</li></ul>
          <button type="button" class="play-cta" data-play-action="host" ${model.online ? '' : `disabled title="${t('Hosting needs a connection to race control')}"`}>${t('HOST A LOBBY')} <b>→</b></button>
        </section>
        <section class="play-mode play-offline" aria-labelledby="play-offline-title">
          <header><i aria-hidden="true">◇</i><div><small>${t('SINGLE PLAYER')}</small><h2 id="play-offline-title">${t('OFFLINE')}</h2></div></header>
          <p>${t('Race AI rivals on this device. No network, no waiting — the full simulation runs locally.')}</p>
          <ul class="play-points"><li>${t('Every track, format and weather')}</li><li>${t('Rookie, Sport or Pro per rival')}</li><li>${t('Arcade Lab rule sets')}</li></ul>
          <button type="button" class="play-cta" data-play-action="offline">${t('RACE OFFLINE')} <b>→</b></button>
        </section>
      </div>
      <section class="play-events" aria-labelledby="play-events-title">
        <header class="wizard-section-head"><h2 id="play-events-title">${t('EVENTS')}</h2><small>${t('Curated offline races · open in the lobby so you can still tweak them')}</small></header>
        <div class="play-events-grid" id="play-events">${model.eventsHtml}</div>
      </section>
      <details class="garage-career-drawer play-career" id="garage-career-drawer">
        <summary><span class="career-level" id="garage-career-level">${t('CAREER')}</span><span><small>${t('LOCAL STREET CAREER')}</small><b id="garage-career-status">${escapeHtml(model.careerSummary)}</b></span><em>${t('OPEN')} <i>⌄</i></em></summary>
        <div class="garage-career-content" id="garage-career-content" data-mounted="true">${model.careerHtml}</div>
      </details>
      <details class="play-reset-drawer">
        <summary>${t('DATA ON THIS DEVICE')} <i aria-hidden="true">⌄</i></summary>
        <section class="play-reset" aria-labelledby="play-reset-title">
          <div><h2 id="play-reset-title">${t('START FRESH')}</h2><p>${t('Deletes everything NIGHTSHIFT stores on this device — driver, cars and liveries, lobby setups, settings and key bindings, career, ghosts and saved races — then restarts at step 1.')}</p></div>
          <button type="button" class="play-reset-button" data-play-action="reset">${t('RESET LOCAL DATA')}</button>
        </section>
      </details>
    </div>`;
}

export function lobbyBrowserHtml(model: Pick<PlayStepModel, 'lobbies' | 'lobbiesError' | 'maps' | 'online'>): string {
  if (!model.online) return `<p class="lobby-browser-empty">${t('You are offline. Public lobbies appear here once you reconnect.')}</p>`;
  if (model.lobbies === null) return `<p class="lobby-browser-empty"><span class="button-spinner" aria-hidden="true"></span>${t('Looking for public lobbies…')}</p>`;
  if (model.lobbiesError) return `<p class="lobby-browser-empty">${escapeHtml(t(model.lobbiesError))}</p>`;
  if (model.lobbies.length === 0) {
    return `<p class="lobby-browser-empty"><b>${t('No public lobbies right now.')}</b> ${t('Host one and switch it to public — it shows up here for everyone.')}</p>`;
  }
  return `<ul class="lobby-browser-list">${model.lobbies.map((lobby) => {
    const map = model.maps.find((candidate) => candidate.id === lobby.mapId);
    const open = lobby.phase === 'lobby' && lobby.playerCount < lobby.maxPlayers;
    return `<li style="--map-accent:${safeColour(map?.palette.accent ?? '#57e9ff')}">
      <span class="lobby-browser-dot" data-phase="${lobby.phase}" aria-hidden="true"></span>
      <span class="lobby-browser-copy"><b>${t("{name}'s lobby", { name: escapeHtml(lobby.hostName) })}</b><small>${escapeHtml(map?.name ?? lobby.mapId)} · ${escapeHtml(t(eventModeLabel(lobby.eventMode)))} · ${t(lobby.timeOfDay.toUpperCase())} · ${t(lobby.weather.toUpperCase())}</small></span>
      <span class="lobby-browser-count">${lobby.playerCount}/${lobby.maxPlayers}<small>${lobby.npcCount > 0 ? t('+{n} AI', { n: lobby.npcCount }) : t('DRIVERS')}</small></span>
      ${open
        ? `<button type="button" data-join-lobby="${escapeHtml(lobby.id)}">${t('JOIN')}</button>`
        : `<button type="button" data-watch-lobby="${escapeHtml(lobby.id)}">${t('WATCH LIVE ◎')}</button>`}
    </li>`;
  }).join('')}</ul>`;
}

function inviteCardHtml(model: PlayStepModel): string {
  const invite = model.invite!;
  const room = invite.room;
  const map = room ? model.maps.find((candidate) => candidate.id === room.settings.mapId) : undefined;
  const host = room?.members?.find((member) => member.host);
  const joinable = room && !invite.error && room.phase === 'lobby' && room.playerCount < room.settings.maxPlayers;
  const status = invite.error
    ? t(invite.error)
    : !room ? t('Checking the invitation…')
      : room.phase !== 'lobby' ? t('This race is already running — you can watch it live.')
        : room.playerCount >= room.settings.maxPlayers ? t('This lobby is full right now.')
          : t('{players} / {max} drivers · {npcs} AI rivals', { players: room.playerCount, max: room.settings.maxPlayers, npcs: room.settings.npcCount });
  return `
    <aside class="play-invite" style="--map-accent:${safeColour(map?.palette.accent ?? '#57e9ff')}" aria-live="polite">
      <i aria-hidden="true">↗</i>
      <span><small>${t("YOU'RE INVITED")}</small><b>${host ? t("{name}'s lobby", { name: escapeHtml(host.name) }) : t('Private lobby')}${map ? ` · ${escapeHtml(map.name)}` : ''}</b><em>${escapeHtml(status)}</em></span>
      <span class="play-invite-actions">
        ${room && room.phase !== 'lobby' && !invite.error ? `<button type="button" class="play-cta ghost" data-play-action="watch">${t('WATCH LIVE ◎')}</button>` : ''}
        <button type="button" class="play-cta" data-play-action="join-invite" ${joinable ? 'data-pad-default' : 'disabled'}>${t('JOIN LOBBY')} <b>→</b></button>
        <button type="button" class="play-dismiss" data-play-action="dismiss-invite" aria-label="${t('Dismiss invitation')}">×</button>
      </span>
    </aside>`;
}

/** Accepts a full invite URL or just its /race/… path. */
export function parseInviteInput(value: string): { roomId: string; inviteToken: string } | null {
  const match = value.trim().match(/\/race\/([a-zA-Z0-9_-]{1,100})\/?\?(?:[^#]*&)?invite=([a-zA-Z0-9_-]{8,200})/);
  return match?.[1] && match[2] ? { roomId: match[1], inviteToken: match[2] } : null;
}
